/**
 * EdenDate wallet / premium UI (chat list header + premium page).
 * Injected as plain DOM so it works regardless of the Solid tab internals.
 */
import rootScope from '@lib/rootScope';
import I18n, {i18n} from '@lib/langPack';

type MeResp = {
  ok?: boolean;
  user_id: number;
  balance: string;
  premium: {active: boolean; expires_at: number};
  official_user_id: number;
  premium_price: string;
};

const API = '/edenapi/api';
let me: MeResp | null = null;
let bar: HTMLElement | null = null;

const PRIVILEGES = [
  'Eden.Priv.FreePaidGroups',
  'Eden.Priv.DmStrangers',
  'Eden.Priv.Broadcast',
  'Eden.Priv.CreatePaidGroups'
];

function myId(): number {
  try {
    return Number((rootScope as any).myId) || 0;
  } catch(e) {
    return 0;
  }
}

async function loadMe(force = false): Promise<MeResp | null> {
  const id = myId();
  if(!id) return null;
  if(me && !force) return me;
  try {
    const res = await fetch(`${API}/me?user_id=${id}`);
    const data = await res.json();
    if(data && data.ok) {
      me = data;
      return me;
    }
  } catch(e) {}
  return me;
}

function money(v: string) {
  const n = Number(v || 0);
  return n.toFixed(2);
}

function t(key: string): string {
  try {
    const out = (I18n as any).format(key, true);
    return typeof out === 'string' ? out : key;
  } catch(e) {
    return key;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) {
  const e = document.createElement(tag);
  if(cls) e.className = cls;
  if(text !== undefined) e.textContent = text;
  return e;
}

// ---------------------------------------------------------------- header bar
function buildBar(): HTMLElement {
  const wrap = el('div', 'eden-wallet-bar');

  const left = el('div', 'eden-wallet-left');
  const label = el('div', 'eden-wallet-label', t('Eden.Balance'));
  const value = el('div', 'eden-wallet-value', money(me?.balance || '0'));
  left.appendChild(label);
  left.appendChild(value);

  const right = el('div', 'eden-wallet-actions');

  const topup = el('button', 'eden-chip', t('Eden.TopUp'));
  topup.addEventListener('click', () => openTopUp());

  const premium = el('button', 'eden-chip eden-chip-primary', t('Eden.Premium'));
  premium.addEventListener('click', () => openPremium());

  right.appendChild(topup);
  right.appendChild(premium);

  wrap.appendChild(left);
  wrap.appendChild(right);
  return wrap;
}

export async function mountWalletBar() {
  const existing = document.querySelector('.eden-wallet-bar') as HTMLElement | null;
  if(existing) {
    bar = existing;
    return;
  }
  await loadMe();

  // make sure the official support chat exists (pinned server-side)
  const id = myId();
  if(id) {
    fetch(`${API}/official/welcome`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({user_id: id})
    }).catch(() => {});
  }

  const host = document.querySelector('.chatlist-top') as HTMLElement;
  if(!host) return;

  const inHost = host.querySelector('.eden-wallet-bar') as HTMLElement | null;
  if(inHost) {
    bar = inHost;
    document.querySelectorAll('.eden-wallet-bar').forEach((e) => { if(e !== inHost) e.remove(); });
    return;
  }

  // drop strays left in detached containers
  document.querySelectorAll('.eden-wallet-bar').forEach((e) => e.remove());

  bar = buildBar();
  host.style.position = host.style.position || 'relative';
  // keep it at the very top, above the contacts strip
  host.insertBefore(bar, host.firstChild);
}

export function refreshWallet() {
  loadMe(true).then(() => {
    if(!bar) return;
    const v = bar.querySelector('.eden-wallet-value');
    if(v) v.textContent = money(me?.balance || '0');
  });
}

// ---------------------------------------------------------------- modals
function modal(title: string, body: HTMLElement[], actions: HTMLElement[]) {
  const overlay = el('div', 'eden-modal-overlay');
  const card = el('div', 'eden-modal');

  const h = el('div', 'eden-modal-title', title);
  card.appendChild(h);

  body.forEach((b) => card.appendChild(b));

  const footer = el('div', 'eden-modal-actions');
  actions.forEach((a) => footer.appendChild(a));
  card.appendChild(footer);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => { if(e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
  return overlay;
}

export function openTopUp() {
  const info = el('div', 'eden-modal-text',
    t('Eden.TopUp.Pending'));
  const close = el('button', 'eden-chip', t('Eden.Close'));
  const overlay = modal(t('Eden.TopUp'), [info], [close]);
  close.addEventListener('click', () => overlay.remove());
}

export async function openPremium() {
  const data = await loadMe(true);
  const price = data?.premium_price || '100.00';
  const active = !!data?.premium?.active;

  const priceLine = el('div', 'eden-price', `${money(price)} U`);
  const sub = el('div', 'eden-modal-text',
    active ? t('Eden.Premium.Active') : t('Eden.Premium.Desc'));

  const list = el('ul', 'eden-priv-list');
  PRIVILEGES.forEach((k) => {
    const li = el('li', undefined, t(k));
    list.appendChild(li);
  });

  const buy = el('button', 'eden-chip eden-chip-primary',
    active ? t('Eden.Close') : t('Eden.Premium.Buy'));
  const overlay = modal(t('Eden.Premium'), [priceLine, sub, list], [buy]);

  buy.addEventListener('click', async () => {
    if(active) {
      overlay.remove();
      return;
    }
    buy.setAttribute('disabled', '1');
    try {
      const res = await fetch(`${API}/premium/buy`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({user_id: myId()})
      });
      const out = await res.json();
      if(out && out.ok) {
        await loadMe(true);
        refreshWallet();
        overlay.remove();
      } else {
        sub.textContent = (out && out.error) || t('Eden.Premium.NoBalance');
        buy.removeAttribute('disabled');
      }
    } catch(e) {
      buy.removeAttribute('disabled');
    }
  });
}

export function getPremiumState() {
  return me?.premium?.active || false;
}

export function getOfficialId() {
  return me?.official_user_id || 0;
}

/** False when the user may not DM this peer (stranger + not premium). */
export async function ensureDmAllowed(peerId: number): Promise<boolean> {
  const id = myId();
  if(!id || !peerId) return true;
  if(peerId === getOfficialId()) return true;
  try {
    const res = await fetch(`${API}/dm/check?user_id=${id}&peer_id=${peerId}`);
    const data = await res.json();
    if(data && data.ok) return !!data.allowed;
  } catch(e) {}
  return true;
}

export function showDmBlockedModal(onUpgrade?: () => void) {
  const text = el('div', 'eden-modal-text', t('Eden.DmBlocked'));
  const upgrade = el('button', 'eden-chip eden-chip-primary', t('Eden.Premium'));
  const overlay = modal(t('Eden.DmBlocked.Title'), [text], [upgrade]);
  upgrade.addEventListener('click', () => {
    overlay.remove();
    if(onUpgrade) onUpgrade();
    openPremium();
  });
}

export function openPaidGroup() {
  const idInput = el('input', 'eden-input') as HTMLInputElement;
  idInput.placeholder = t('Eden.PaidGroup.ChatId');

  const priceInput = el('input', 'eden-input') as HTMLInputElement;
  priceInput.type = 'number';
  priceInput.min = '0';
  priceInput.step = '0.01';
  priceInput.placeholder = t('Eden.PaidGroup.Price');

  const hint = el('div', 'eden-modal-text', t('Eden.PaidGroup.Hint'));
  const save = el('button', 'eden-chip eden-chip-primary', t('Eden.Save'));
  const close = el('button', 'eden-chip', t('Eden.Close'));
  const overlay = modal(t('Eden.PaidGroup'), [hint, idInput, priceInput], [close, save]);

  close.addEventListener('click', () => overlay.remove());
  save.addEventListener('click', async () => {
    const chatId = Number(idInput.value);
    const price = Number(priceInput.value);
    if(!chatId || !price || price <= 0) return;
    save.setAttribute('disabled', '1');
    try {
      const res = await fetch(`${API}/group/paid`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({chat_id: chatId, owner_id: myId(), price})
      });
      const out = await res.json();
      if(out && out.ok) {
        hint.textContent = t('Eden.PaidGroup.Saved');
        save.removeAttribute('disabled');
      } else {
        hint.textContent = (out && out.error) || 'failed';
        save.removeAttribute('disabled');
      }
    } catch(e) {
      save.removeAttribute('disabled');
    }
  });
}

const paidCache = new Set<number>();

/** True when the user may participate (paid groups charge once per session). */
export async function ensurePaidGroup(chatId: number): Promise<boolean> {
  const id = myId();
  if(!id || !chatId || paidCache.has(chatId)) return true;

  try {
    const info = await (await fetch(`${API}/group/paid/${chatId}`)).json();
    if(!info || !info.ok || !info.paid) {
      paidCache.add(chatId);
      return true;
    }
    const res = await (await fetch(`${API}/group/join`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({chat_id: chatId, user_id: id})
    })).json();

    if(res && res.ok) {
      paidCache.add(chatId);
      if(res.charged && res.charged !== '0.00') refreshWallet();
      return true;
    }

    showPaidGroupModal(info.price || '0.00');
    return false;
  } catch(e) {
    return true;
  }
}

export function showPaidGroupModal(price: string) {
  const text = el('div', 'eden-modal-text', t('Eden.PaidGroup.NeedPay').replace('%s', price));
  const ok = el('button', 'eden-chip eden-chip-primary', t('Eden.Close'));
  const overlay = modal(t('Eden.PaidGroup'), [text], [ok]);
  ok.addEventListener('click', () => overlay.remove());
}

export function ensureOfficialChat(tries = 12) {
  const id = myId();
  if(!id) {
    if(tries > 0) setTimeout(() => ensureOfficialChat(tries - 1), 2000);
    return;
  }
  fetch(`${API}/official/welcome`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({user_id: id})
  }).then((r) => r.json()).then((out) => {
    if(out && out.created) {
      // a new dialog appeared — let the chat list refresh itself
      setTimeout(() => { try { (rootScope as any).dispatchEvent('dialogs_multiupdate'); } catch(e) {} }, 800);
    }
  }).catch(() => {});
}

export function ensureWalletUi() {
  ensureOfficialChat();
  setTimeout(() => mountWalletBar(), 1200);
  const obs = new MutationObserver(() => {
    if(!bar || !bar.isConnected) mountWalletBar();
  });
  obs.observe(document.body, {childList: true, subtree: true});
}
